import { useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../contexts/PermissionsContext';
import { getSpaCommunicationTargetRecords, sendSpaCommunication } from '../APIs/SpaAPI';
import { COMMUNICATION_CHANNELS, COMMUNICATION_TARGETS, communicationResult, communicationSendPayload } from '../utils/spaCommunications';

export default function SpaCommunicationSendDialog({ open, onClose, token, tenant, capabilities, target, onResult }) {
  const { t, i18n } = useTranslation();
  const ct = (key, options) => t(`spa.communications.${key}`, options);
  const { can, loading } = usePermissions();
  const allowed = !loading && can('spa-communications:view') && can('spa-communications:send');
  const [type, setType] = useState(target?.type || 'appointmentId');
  const [id, setId] = useState(target?.id ? String(target.id) : '');
  const [event, setEvent] = useState(COMMUNICATION_TARGETS[target?.type || 'appointmentId'][0]);
  const [channel, setChannel] = useState('EMAIL');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [records, setRecords] = useState(null);
  const [reload, setReload] = useState(0);
  const active = useRef(false);
  const lock = useRef(false);
  const scope = `${token}:${tenant}:${allowed}:${open}:${target?.type}:${target?.id}`;
  const currentScope = useRef(scope); currentScope.current = scope;
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const canLookup = !target && allowed && (['appointmentId', 'waitlistOfferId'].includes(type) ? can('spa-appointments:view') : can('spa-finance:view'));
  const lookupKey = `${type}:${token}:${tenant}:${canLookup}`;
  const currentRecords = records?.key === lookupKey ? records : null;
  const options = currentRecords?.items || [];
  useEffect(() => {
    if (!open || !canLookup || !token || !tenant) return undefined;
    const controller = new AbortController();
    setRecords({ key: lookupKey, loading: true });
    getSpaCommunicationTargetRecords(type, token, tenant, { signal: controller.signal }).then((data) => {
      if (!Array.isArray(data) || data.some((item) => !Number.isInteger(item.id))) throw new Error(t('spa.communications.invalidResponse'));
      if (!controller.signal.aborted) setRecords({ key: lookupKey, items: data });
    }).catch((failure) => { if (!controller.signal.aborted) setRecords({ key: lookupKey, error: failure.message }); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canLookup, token, tenant, type, lookupKey, reload]);
  const recordLabel = (item) => {
    const client = item.client || item.request?.client;
    const name = [client?.name, client?.surname].filter(Boolean).join(' ') || item.clientName || '';
    const detail = item.code || item.package?.name || item.packageNameSnapshot || item.snapshot?.treatmentName || item.treatment?.name
      || item.request?.treatment?.product?.name || '';
    const date = item.startAt && new Date(item.startAt);
    return [name, detail, date && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
      dateStyle: 'short', timeStyle: 'short', ...(capabilities?.timeZone ? { timeZone: capabilities.timeZone } : {}),
    }).format(date) : '', `#${item.id}`].filter(Boolean).join(' | ');
  };
  const payload = communicationSendPayload(type, id, event, channel);
  const ready = capabilities?.channels?.[channel]?.ready === true;
  const send = async (submit) => {
    submit.preventDefault();
    if (!allowed || !open || !token || !tenant || !payload || !ready || lock.current || result) return;
    const submittedScope = scope;
    lock.current = true; setSaving(true); setError('');
    try {
      const response = communicationResult(await sendSpaCommunication(payload, token, tenant));
      if (!active.current || currentScope.current !== submittedScope) return;
      if (!response) throw new Error(ct('invalidResponse'));
      setResult(response); onResult?.(response);
    } catch (failure) {
      if (active.current && currentScope.current === submittedScope) setError(failure.message || ct('sendError'));
    } finally {
      lock.current = false;
      if (active.current && currentScope.current === submittedScope) setSaving(false);
    }
  };
  if (!allowed) return null;
  return <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
    <DialogTitle>{ct('newCommunication')}</DialogTitle>
    <form onSubmit={send}>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <TextField select fullWidth label={ct('targetType')} value={type} disabled={saving || Boolean(result) || Boolean(target)}
          onChange={(change) => { setType(change.target.value); setEvent(COMMUNICATION_TARGETS[change.target.value][0]); setId(''); setError(''); }}>
          {Object.keys(COMMUNICATION_TARGETS).map((key) => <MenuItem key={key} value={key}>{ct(`targets.${key}`)}</MenuItem>)}
        </TextField>
        {canLookup ? <Autocomplete options={options} value={options.find((item) => String(item.id) === id) || null}
          loading={currentRecords?.loading} disabled={saving || Boolean(result)} getOptionLabel={recordLabel} isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setId(value ? String(value.id) : '')}
          noOptionsText={ct('noRecords')} renderInput={(params) => <TextField {...params} required label={ct('record')} />} />
          : <TextField fullWidth required type="number" label={ct('targetId')} value={id} disabled={saving || Boolean(result) || Boolean(target)}
            helperText={!target ? ct('recordSearchDenied') : undefined} inputProps={{ min: 1, step: 1 }} onChange={(change) => setId(change.target.value)} />}
        {canLookup && currentRecords?.error && <Alert severity="error" action={<Button color="inherit" onClick={() => setReload((value) => value + 1)}>{ct('retry')}</Button>}>{currentRecords.error}</Alert>}
        <TextField select fullWidth label={ct('event')} value={event} disabled={saving || Boolean(result)} onChange={(change) => setEvent(change.target.value)}>
          {COMMUNICATION_TARGETS[type].map((key) => <MenuItem key={key} value={key}>{ct(`events.${key}`)}</MenuItem>)}
        </TextField>
        <TextField select fullWidth label={ct('channel')} value={channel} disabled={saving || Boolean(result)} onChange={(change) => setChannel(change.target.value)}>
          {COMMUNICATION_CHANNELS.map((key) => <MenuItem key={key} value={key} disabled={key !== 'EMAIL' || capabilities?.channels?.[key]?.ready !== true}>
            {ct(`channels.${key}`)}{capabilities?.channels?.[key]?.ready !== true ? ` (${ct('providerUnavailable')})` : ''}
          </MenuItem>)}
        </TextField>
        {!ready && <Alert severity="warning">{ct('channelNotReady')}</Alert>}
        {!result && <Alert severity="warning">{ct('sendConfirm')}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {result && <Alert severity={result.status === 'SENT' ? 'success' : result.status === 'PENDING' || result.status === 'SENDING' ? 'info' : 'warning'}>
          {ct(`outcomes.${result.status}`)}{result.reason ? ` ${ct(`reasons.${result.reason}`, { defaultValue: result.reason })}` : ''}
        </Alert>}
      </Stack></DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{ct(result ? 'close' : 'cancel')}</Button>
        {!result && <Button type="submit" variant="contained" disabled={!payload || !ready || saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>{ct('queueMessage')}</Button>}
      </DialogActions>
    </form>
  </Dialog>;
}
